import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Pencil, Trash2, Plus } from "lucide-react";

const empty = { title: "", preacher: "", date: "", youtube_url: "", description: "", series: "" };

export default function AdminSermons() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/sermons").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/sermons/${editing.id}`, f);
        toast.success("Sermon updated");
      } else {
        await api.post("/sermons", f);
        toast.success("Sermon added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sermon?")) return;
    await api.delete(`/sermons/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-sermons-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="overline text-terracotta mb-2">Content</p>
          <h1 className="font-heading font-black text-4xl tracking-tighter text-olive">Sermons</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditing(null); setF(empty); setOpen(true); }} className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none" data-testid="new-sermon-btn">
              <Plus size={16} className="mr-1" /> New Sermon
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit sermon" : "New sermon"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="sermon-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Preacher</Label>
                  <Input required value={f.preacher} onChange={(e) => setF({ ...f, preacher: e.target.value })} data-testid="sermon-preacher" />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="sermon-date" />
                </div>
              </div>
              <div>
                <Label>YouTube URL</Label>
                <Input required placeholder="https://youtube.com/watch?v=..." value={f.youtube_url} onChange={(e) => setF({ ...f, youtube_url: e.target.value })} data-testid="sermon-yt" />
              </div>
              <div>
                <Label>Series (optional)</Label>
                <Input placeholder="e.g. Occupy and Build" value={f.series || ""} onChange={(e) => setF({ ...f, series: e.target.value })} data-testid="sermon-series" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={3} value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="sermon-desc" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={loading} className="bg-olive hover:bg-olive-hover text-bone rounded-none" data-testid="sermon-save">
                  {loading ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream border-b border-border">
            <tr className="text-left">
              <th className="p-4 font-semibold">Title</th>
              <th className="p-4 font-semibold">Preacher</th>
              <th className="p-4 font-semibold">Date</th>
              <th className="p-4 font-semibold w-36">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-muted_ink">No sermons yet</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-b border-border last:border-0">
                <td className="p-4 font-medium text-ink">{it.title}</td>
                <td className="p-4 text-muted_ink">{it.preacher}</td>
                <td className="p-4 text-muted_ink">{it.date}</td>
                <td className="p-4 flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(it); setF({ ...empty, ...it }); setOpen(true); }}><Pencil size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(it.id)} className="text-destructive hover:text-destructive"><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
